import './index.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { registerSW } from 'virtual:pwa-register';
import { router } from './router';
import { queryClient } from './lib/queryClient';
import { startSyncLoop } from './sync/syncEngine';
import { useThemeStore } from './stores/themeStore';
import { primeAudioOnFirstGesture } from './lib/sounds';

// Apply persisted theme as soon as the bundle parses so we never flash light.
useThemeStore.getState().hydrate();

// Background reconcile when the device is online — every shell benefits.
startSyncLoop();

// Browsers gate AudioContext until a user gesture. Warm it up so the first
// real chime (new ticket, sale, etc.) fires without lag.
primeAudioOnFirstGesture();

// ─── Service worker auto-update ────────────────────────────────────────────
// vite-plugin-pwa is configured with registerType='autoUpdate' so a newly
// deployed build replaces the cached one immediately on the next page load.
// onNeedRefresh fires when the new SW finishes installing — we toast the
// user so an in-flight POS session can pick the moment to reload instead
// of getting interrupted. checkForUpdateInterval polls every 60s when the
// tab is open so long-lived sessions notice deploys quickly.
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    toast(
      (t) => (
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement('div', null,
            React.createElement('div', { className: 'font-semibold text-sm' }, 'New version available'),
            React.createElement('div', { className: 'text-xs text-fg-muted' }, 'Reload to get the latest changes.'),
          ),
          React.createElement('button', {
            onClick: () => { toast.dismiss(t.id); updateSW(true); },
            className: 'inline-flex items-center rounded-md bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold px-2.5 py-1.5 transition-colors',
          }, 'Reload'),
        )
      ),
      { duration: Infinity, position: 'top-right' },
    );
  },
  onOfflineReady() { /* no-op — installation toast adds noise */ },
});

if (typeof window !== 'undefined') {
  // Re-check for a new SW every minute so a long-running POS / KDS shift
  // gets the update toast without anyone reloading the tab.
  setInterval(() => { updateSW(); }, 60_000);
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'hsl(var(--surface))',
            color: 'hsl(var(--fg))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 'var(--radius)',
            fontSize: '14px',
          },
        }}
      />
    </QueryClientProvider>
  </React.StrictMode>,
);
